import { useState, useEffect, useContext, useRef, ChangeEvent } from "react";
import Box from "@mui/material/Box";
import { useParams, useNavigate } from "react-router";
import IEventSession from "../../dataProvider/interfaces/IEventSession";
import { StoreContext } from "../../store/Store";
import { Loading, useRedirect } from "react-admin";
import { routePaths } from "../../baseLayout/SidebarMenu";
import CloseIcon from "@mui/icons-material/Close";
import NotificationMessage, {
  notificationType,
} from "../../components/NotificationMessage/NotificationMessage";
import { APIContext } from "../../store/APIStore";
import {
  actionDialogType,
  ComboDropdown,
  LabelWithTooltip,
  RouteLeavingGuard,
  SimpleActionDialog,
} from "../../components/index";
import ISessionLocation from "../../dataProvider/interfaces/ISessionLocation";
import { BiTrash } from "react-icons/bi";
import { useHasPermissions } from "../../hooks/useHasPermissions";
import { PERMISSIONS } from "../../roles";
import { v4 as uuidv4 } from "uuid";
import { theme } from "../../theme";
import { DateTimePicker } from "@mui/x-date-pickers";
import FdTextField from "../../components/UIComponents/TextField/FdTextfield";
import FdTypography from "../../components/UIComponents/Typography/FdTypography";
import FdButton from "../../components/UIComponents/Button/FdButton";
import FdIconButton from "../../components/UIComponents/IconButton/FdIconButton";
import { useForm, Controller } from "react-hook-form";
import FdCheckbox from "../../components/UIComponents/Checkbox/FdCheckbox";
import { convertToUTC } from "../../utils";
import moment from "moment";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import RadioGroup from "@mui/material/RadioGroup";
import { Checkbox, FormControlLabel, Radio } from "@mui/material";

const classes = {
  wrapper: {
    display: "flex",
    justifyContent: "space-between",
  },
  contentWrapper: {
    height: "calc(100vh - 105px)",
    display: "flex",
    flexDirection: "column",
    // width: "74%", the width of the content part if we show the badge preview
    width: "100%",
  },
  topActions: {
    display: "flex",
    alignItems: "center",
    paddingBottom: "20px",
  },
  infoTitle: {
    display: "flex",
    flexGrow: 1,
    alignItems: "center",
  },
  saveButton: {
    marginLeft: "20px",
    marginRight: "10px",
  },
  formContent: {
    position: "relative",
    width: "100%",
    flexGrow: 1,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    overflow: "auto",
  },
  genericFields: {
    width: "50%",
    height: "100%",
    backgroundColor: theme.palette.grey[600],
    overflow: "auto",
    borderRadius: "5px",
  },
  formTitle: {
    backgroundColor: "#fff",
    padding: "20px",
  },
  formFields: {
    padding: "20px",
    textAlign: "left",
    "& .MuiFormControl-root": {
      mb: 0.5,
    },
    "& .MuiFormHelperText-root ": {
      mx: 0,
    },
  },
  inputIcon: {
    position: "absolute",
    right: "7px",
    top: "50%",
    fontSize: "18px",
    color: theme.palette.grey["800"],
  },
  dateInput: {
    marginBottom: "15px",
  },
  selectField: {
    marginBottom: "15px",
  },
  checkbox: {
    color: theme.palette.grey["800"],
  },
  fdRadio: {
    "&.MuiFormControlLabel-root": { alignItems: "flex-start", mr: 3 },
    "& .label": { color: "#000", fontSize: "14px" },
    "& .subtext": { fontSize: "12px" },
    "& .MuiRadio-root": {
      p: "0 9px",
    },
  },
};
const customStyle = {
  "& .MuiInputBase-root.MuiFilledInput-root": {
    border: "1px solid #E7E7E7",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.05)",
    borderRadius: "6px !important",
    padding: "12px 10px",
    background: "#fff",
    "&:before,&:after": {
      display: "none",
    },
    "&:hover": {
      background: "#fff",
    },
    "& input": {
      border: 0,
      boxShadow: "none",
      padding: "0 !important",
    },
  },

  "& .MuiButtonBase-root ": {
    "& svg": {
      height: "18px",
      width: "18px",
      color: "#A8A8A8",
    },
  },
  "&.noPastErrorStyle .Mui-error": {
    color: theme.palette.text.primary,
  },
  "&.noPastErrorStyle .Mui-error.MuiFormHelperText-root ": {
    color: theme.palette.error.main,
  },
};

interface IEventLocation {
  label: string;
  value: number;
  inputValue?: string;
}

const EventSessionEdit = () => {
  const [state, dispatch] = useContext(StoreContext);
  const [eventLocations, setEventLocations] = useState<IEventLocation[]>([]);
  const [sessionInfo, setSessionInfo] = useState<IEventSession | null>(null);
  const [sessionLocation, setSessionLocation] = useState<IEventLocation | null>(
    null
  );
  const [confirmationDialog, setConfirmationDialog] = useState<any>({
    open: false,
    title: "",
    type: actionDialogType.REGULAR,
    isLoading: false,
    onConfirm: () => {},
  });
  const [initialValues, setInitialValues] = useState<any>({
    // We start with a random third party ID, if existing session is being modified, that third party ID will overwrite this value
    thirdPartyId: uuidv4(),
    startsOn: new Date(),
    endsOn: new Date(),
    checkCapacityLimit: false,
    accessAmount: "",
    allowScanningOut: false,
    allowForcedCheckIn: false,
  });
  const [loading, setLoading] = useState(true);
  const [dirtyForm, setDirtyForm] = useState(false);
  const { eventId, sessionId } = useParams() as any;
  let navigate = useNavigate();
  const redirect = useRedirect();
  const notificationRef = useRef<any>();
  const api = useContext(APIContext);
  const [isRestricted, setRestricted] = useState(
    initialValues.defaultSessionAccessAmount !== 0
      ? "Attendees only with reservation are allowed"
      : "Open for All"
  );
  const disablePast = true;
  const eventEndStamp = convertToUTC(state?.selectedEvent?.endDateTime || "");
  // permissions
  const canManageSession = useHasPermissions(PERMISSIONS.CAN_MANAGE_SESSION);

  async function loadData() {
    // load selected event
    if (eventId && !state.selectedEvent) {
      await fetchSelectedEvent();
    }

    if (!state.sessionLocations) {
      await fetchSessionLocations();
    }

    // load the session information
    if (sessionId) {
      await fetchSessionInfo();
    }

    setLoading(false);
  }

  useEffect(() => {
    loadData();

    return () => {
      setSessionInfo(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSelectedEvent = async () => {
    try {
      const event = await api.eventService.getEvent({ id: eventId });
      dispatch({ type: "SET_SELECTED_EVENT", payload: event });
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const fetchSessionLocations = async () => {
    try {
      const locations = (await api.locationService.getLocations(
        eventId
      )) as any;
      dispatch({ type: "SET_SESSION_LOCATIONS", payload: locations });
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const fetchSessionInfo = async () => {
    try {
      const session = (await api.sessionService.getSession(sessionId)) as any;
      setSessionInfo(session);
      setFormData(session);
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response?.data?.message,
        notificationType.Error
      );
    }
  };

  useEffect(() => {
    const locations: IEventLocation[] =
      state.sessionLocations?.map((location: ISessionLocation) => ({
        label: location.name,
        value: location.id,
      })) || [];
    setEventLocations(locations);
  }, [state.sessionLocations]);

  useEffect(() => {
    const location = getLocation(sessionInfo?.location);
    setSessionLocation(location);
  }, [eventLocations, sessionInfo?.location]);

  // helper function to format value data to be compatible with combobox input
  const getLocation = (locationId: number | null | undefined) => {
    return locationId
      ? eventLocations.filter(
          (location: IEventLocation) => location.value === locationId
        )[0] || null
      : null;
  };

  const onSubmit = async (values: any) => {
    const formValues = Object.assign({}, values);
    if (values.startsOn && values.endsOn) {
      formValues.startsOn = moment(formValues.startsOn).utcOffset(0, true);
      formValues.endsOn = moment(formValues.endsOn).utcOffset(0, true);
    }

    formValues.defaultSessionAccessAmount = -1;
    formValues.defaultRestrictAccessAmount = 0;

    // handle session location data
    if (sessionLocation) {
      formValues.location = sessionLocation.value || sessionLocation.inputValue;

      // create the location if a new one was filled in
      if (!getLocation(sessionLocation.value) && sessionLocation.inputValue) {
        const location = await api.locationService.createLocation(
          state.selectedEvent?.id,
          {
            name: sessionLocation.inputValue,
            // thirdPartyId: sessionInfo?.thirdPartyId,
            // thirdPartyId: sessionLocation.inputValue?.replace(/\W+/g, ''),
          }
        );
        formValues.location = location.id;

        await fetchSessionLocations();
      }
    }
    if (!formValues.checkCapacityLimit && isRestricted !== "Open for All") {
      formValues.defaultSessionAccessAmount = 0;
      formValues.defaultRestrictAccessAmount = -1;
    }
    if (formValues.checkCapacityLimit) {
      if (isRestricted === "Open for All") {
        formValues.defaultSessionAccessAmount = formValues.accessAmount;
        formValues.defaultRestrictAccessAmount = 0;
      } else {
        formValues.defaultRestrictAccessAmount = formValues.accessAmount;
        formValues.defaultSessionAccessAmount = 0;
      }
    }
    if (sessionInfo) {
      updateSession(formValues);
    } else {
      createSession(formValues);
    }

    setDirtyForm(false);
  };

  const createSession = async (values: any) => {
    try {
      const newSession = await api.sessionService.createSession(
        state.selectedEvent?.id,
        values
      );
      setSessionInfo(newSession);
      notificationRef.current?.show(
        "Session successfully saved",
        notificationType.Success
      );
      navigate(-1);
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const updateSession = async (values: any) => {
    try {
      const newSession = await api.sessionService.updateSession(
        (sessionInfo as IEventSession).id,
        values
      );
      setSessionInfo(newSession);
      setFormData(newSession);
      notificationRef.current?.show(
        "Session successfully saved",
        notificationType.Success
      );
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const showDeleteDialog = () => {
    setConfirmationDialog({
      open: true,
      title: "Are you sure you want to delete this session?",
      icon: <BiTrash size="45" />,
      type: actionDialogType.WARNING,
      isLoading: false,
      onConfirm: () => {
        dirtyForm && setDirtyForm(false); // we don't want to show the form leave warning when redirecting after deletion
        handleDeleteSession();
      },
      onCancel: handleDialogClose,
    });
  };

  const handleDialogClose = () => {
    setConfirmationDialog({
      ...confirmationDialog,
      open: false,
    });
  };

  useEffect(() => {
    if (initialValues.defaultSessionAccessAmount !== 0) {
      setRestricted("Open for All");
    } else if (initialValues.defaultRestrictAccessAmount !== 0) {
      setRestricted("Attendees only with reservation are allowed");
    }
  }, [initialValues]);

  const handleDeleteSession = async () => {
    try {
      if (sessionInfo) {
        await api.sessionService.delete(sessionInfo.id);
        redirect(
          `/${routePaths.EVENTS}/${state.selectedEvent?.id}/${routePaths.SESSIONS}`
        );
      }
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const checkSessionAccessAmount = (obj: IEventSession) => {
    return (
      obj.defaultRestrictAccessAmount > 0 || obj.defaultSessionAccessAmount > 0
    );
  };

  const setFormData = (session: IEventSession) => {
    const checkSessions = checkSessionAccessAmount(session);
    const initialData = {
      name: session?.name,
      // description: session?.description,
      startsOn: convertToUTC(session?.startDateTime),
      endsOn: convertToUTC(session?.endDateTime),
      // mode: session?.mode,
      capacity: session?.maxPeople,
      thirdPartyId: session?.thirdPartyId,
      allowScanningOut: session?.allowScanningOut,
      defaultSessionAccessAmount: session?.defaultSessionAccessAmount,
      allowForcedCheckIn: session?.allowForcedCheckIn,
      defaultRestrictAccessAmount: session?.defaultRestrictAccessAmount,
      accessAmount: !checkSessions
        ? "INFINITE"
        : session?.defaultRestrictAccessAmount !== 0
        ? session?.defaultRestrictAccessAmount
        : session?.defaultSessionAccessAmount,
      checkCapacityLimit: !checkSessions ? false : true,
    };
    setInitialValues(initialData);
  };
  const {
    register,
    handleSubmit,
    formState: { isDirty, errors },
    reset,
    control,
    watch,
    setValue,
  } = useForm({
    defaultValues: initialValues,
  });

  useEffect(() => {
    reset(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, initialValues]);

  if (loading) {
    return <Loading />;
  }

  const sessionType = [
    { id: "1", label: "Open Session", value: "Open for All" },
    {
      id: "2",
      label: "Restricted Session",
      value: "Attendees only with reservation are allowed",
    },
  ];
  const handlePerAttendeeLimit = (
    e: ChangeEvent<HTMLInputElement>,
    onChange: ((arg0: any) => void) | undefined
  ) => {
    setValue("accessAmount", "");
    if (typeof onChange === "function") {
      onChange(e);
    }
  };
  return (
    <>
      <Box sx={classes.wrapper}>
        <Box sx={classes.contentWrapper}>
          <Box sx={classes.topActions}>
            <Box sx={classes.infoTitle}>
              <Box textAlign="left">
                <FdTypography
                  label={sessionInfo?.name || ""}
                  variant="h2"
                  component="h2"
                  style={{ marginBottom: "10px" }}
                />
                {/* {sessionInfo?.thirdPartyId && ( */}
                <FdTypography
                  label={`Third Party ID: ${initialValues?.thirdPartyId}`}
                />
                {/* )} */}
                <FdTypography label={sessionLocation?.label} />
              </Box>
            </Box>

            {sessionInfo && canManageSession && (
              <FdButton
                variant="text"
                sx={{ marginLeft: "15px" }}
                startIcon={<BiTrash />}
                onClick={() => {
                  showDeleteDialog();
                }}
                ButtonName="Delete"
                aria-label="Delete-btn"
              />
            )}

            {canManageSession && (
              <FdButton
                variant="contained"
                color="primary"
                size="medium"
                sx={classes.saveButton}
                ButtonName="Save"
                aria-label="save-btn"
                type="submit"
                form="attendeeForm"
                disabled={!isDirty && !dirtyForm}
              />
            )}
            <FdIconButton
              aria-label="session-close"
              onClick={() => {
                redirect(
                  `/${routePaths.EVENTS}/${state.selectedEvent?.id}/${routePaths.SESSIONS}`
                );
              }}
              Icon={<CloseIcon />}
            />
          </Box>

          <Box sx={classes.formContent}>
            <form
              id="attendeeForm"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              style={{
                width: "100%",
                textAlign: "left",
                height: "100%",
                display: "contents",
              }}
            >
              <Box sx={classes.genericFields}>
                <Box sx={classes.formTitle}>
                  <FdTypography
                    label="Session information"
                    variant="h2"
                    component="h2"
                  />
                </Box>
                <Box sx={classes.formFields}>
                  {/* session name */}
                  <FdTextField
                    register={register}
                    label="Name"
                    name="name"
                    required
                    error={errors.name?.type === "required"}
                    errorMsg={errors.name && "Session name is required"}
                  />

                  {/* session description */}
                  {/* <TextField
                        name="description"
                        label="Description"
                        multiline
                        rows={6}
                        rowsMax={6}
                        InputProps={{
                          className: classes.textField,
                        }}
                      /> */}

                  {/* session location */}

                  <ComboDropdown
                    label="Location"
                    options={eventLocations}
                    initialValue={sessionLocation}
                    onValueChange={(location: any) => {
                      setDirtyForm(true);
                      setSessionLocation(location);
                    }}
                    name={""}
                  />
                  {/* third party id */}
                  {/* {!sessionInfo?.thirdPartyId && (
                    <FdTextField
                      register={register}
                      name="thirdPartyId"
                      label="Third Party ID"
                      // disabled={sessionInfo?.thirdPartyId ? true : false}
                    />
                  )} */}

                  {/* session start date time */}
                  <Controller
                    {...register("startsOn")}
                    control={control}
                    defaultValue={new Date()}
                    render={({ field: { onBlur, name, ...field } }) => (
                      <>
                        <DateTimePicker
                          {...register("startsOn")}
                          label="Start date"
                          {...field}
                          // inputRef={ref}
                          format="yyyy.MM.dd, HH:mm"
                          ampm={false}
                          disablePast={disablePast}
                          className={disablePast ? "noPastErrorStyle" : ""}
                          sx={customStyle}
                        />
                      </>
                    )}
                  />
                  {/* session end date time */}
                  <Controller
                    {...register("endsOn")}
                    control={control}
                    defaultValue={new Date()}
                    rules={{
                      validate: {
                        endsOn: (date) => date > watch().startsOn,
                        endLimit: (date) => convertToUTC(date) < eventEndStamp,
                      },
                    }}
                    render={({ field: { onBlur, name, ...field } }) => (
                      <>
                        <DateTimePicker
                          {...register("endsOn")}
                          label="End date"
                          {...field}
                          // inputRef={ref}
                          format="yyyy.MM.dd, HH:mm"
                          ampm={false}
                          disablePast={disablePast}
                          className={disablePast ? "noPastErrorStyle" : ""}
                          sx={customStyle}
                          minDate={watch().startsOn}
                          slotProps={{
                            textField: {
                              helperText:
                                errors?.endsOn?.type === "endsOn"
                                  ? "Session end date should be greater than Session start date"
                                  : errors?.endsOn?.type === "endLimit"
                                  ? "Session end date and time should be less than Event end date and time"
                                  : "",
                              error:
                                errors?.endsOn?.type === "endsOn" ||
                                errors?.endsOn?.type === "endLimit",
                            },
                          }}
                        />
                      </>
                    )}
                  />
                  <Controller
                    name="sessionType"
                    control={control}
                    // defaultValue={isRestricted}
                    render={({ field }) => (
                      <>
                        <FormControl>
                          <FormLabel
                            id="demo-error-radios"
                            sx={{ mb: 1, fontSize: "12px" }}
                          >
                            Select mode of session
                          </FormLabel>
                          <RadioGroup
                            aria-labelledby="demo-error-radios"
                            sx={{ flexDirection: "row" }}
                            {...field}
                            row
                            onChange={(event, value) => {
                              field.onChange(value);
                              setRestricted(value);
                            }}
                            defaultValue={
                              initialValues.defaultSessionAccessAmount !== 0
                                ? "Open for All"
                                : "Attendees only with reservation are allowed"
                            }
                          >
                            {sessionType.map((item) => {
                              return (
                                <FormControlLabel
                                  sx={classes.fdRadio}
                                  key={item.id}
                                  value={item.value}
                                  control={<Radio />}
                                  // label={item.label}
                                  label={
                                    <>
                                      <Box className="label">{item.label}</Box>
                                      <Box className="subtext">
                                        {item.value}
                                      </Box>
                                    </>
                                  }
                                />
                              );
                            })}
                          </RadioGroup>
                        </FormControl>
                      </>
                    )}
                  />

                  {/* perAttendeeLimit */}

                  <Box mt={2}>
                    <FdTypography
                      label={
                        <LabelWithTooltip
                          label="Limit no. of scans?"
                          hint="Check the box below if the no. Of scans is to be limited"
                        />
                      }
                      sx={{ fontSize: "12px" }}
                    />
                    <Box display="flex" alignItems="center">
                      <Controller
                        control={control}
                        {...register("checkCapacityLimit")}
                        defaultValue={false}
                        render={({ field: { onChange, value } }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={value}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                  handlePerAttendeeLimit(e, onChange)
                                }
                                defaultChecked={initialValues.accessAmount > 0}
                              />
                            }
                            label={undefined}
                          />
                        )}
                      />
                      <FdTextField
                        register={register}
                        type="text"
                        name="accessAmount"
                        sx={{ ml: "-16px" }}
                        disabled={!watch().checkCapacityLimit}
                        value={
                          !watch().checkCapacityLimit
                            ? "INFINITE"
                            : watch().accessAmount
                        }
                        placeholder="No. of coupons/products handed out"
                        validationObject={{
                          zeroCheck: (v: string) =>
                            watch().checkCapacityLimit ? parseInt(v) > 0 : null,
                          decimalCheck: (v: string) =>
                            watch().checkCapacityLimit
                              ? v.toString().indexOf(".") === -1
                              : null,
                        }}
                        error={
                          errors.accessAmount?.type === "zeroCheck" ||
                          errors.accessAmount?.type === "decimalCheck"
                        }
                        errorMsg={
                          (errors.accessAmount?.type === "zeroCheck"
                            ? "Number should be greater than 0"
                            : "") ||
                          (errors.accessAmount?.type === "decimalCheck"
                            ? "Number should not be decimal"
                            : "")
                        }
                      />
                    </Box>
                  </Box>
                  {/* capacity limit */}
                  <FdTextField
                    register={register}
                    type="number"
                    name="capacity"
                    label={
                      <LabelWithTooltip
                        label="Room Capacity Limit"
                        hint="-1 -> for Infinite access,
                        0 -> for no access,
                        1 or x number -> for (x) times check ins."
                      />
                    }
                  />

                  <FdCheckbox
                    register={register}
                    name="allowScanningOut"
                    label="Allow OUT-scanning"
                    value={initialValues.allowScanningOut}
                    sx={{ color: theme.palette.grey["800"] }}
                    defaultValue={initialValues.allowScanningOut}
                    control={control}
                  />

                  <FdCheckbox
                    register={register}
                    name="allowForcedCheckIn"
                    label="Allow force check-in"
                    value={initialValues.allowForcedCheckIn}
                    sx={{ color: theme.palette.grey["800"] }}
                    defaultValue={initialValues.allowForcedCheckIn}
                    control={control}
                  />
                </Box>
              </Box>
              <RouteLeavingGuard
                when={isDirty}
                navigate={(path: any) => navigate(path)}
                shouldBlockNavigation={(location: any) => isDirty}
              />
            </form>
          </Box>
        </Box>
      </Box>

      <NotificationMessage ref={notificationRef} />

      {confirmationDialog.open && (
        <SimpleActionDialog
          type={confirmationDialog.type}
          icon={confirmationDialog.icon}
          open={confirmationDialog.open}
          title={confirmationDialog.title}
          isLoading={confirmationDialog.isLoading}
          onConfirm={confirmationDialog.onConfirm}
          onCancel={confirmationDialog.onCancel}
          onClose={handleDialogClose}
          content={confirmationDialog.content}
          confirmLabel={confirmationDialog.confirmLabel}
        />
      )}
    </>
  );
};

export default EventSessionEdit;
